import { PainelEstampadoDetalhado, FixacoesTorreDetalhadas } from '../anatomia/DetalhesMecanicos3D'
import { CaixaDeRoda3D } from './CaixaDeRoda3D'
import { vetor } from '../../dominio/geometria/vetor3'
import {
  raioArcoRoda,
  torreSuspensao,
  tunelCentral,
  xDaEstrutura,
  xDaLongarina,
  yDaLongarina,
  zCaixaDeAr,
  zParaFogo,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { Barra } from '../componentes/Barra'
import { BarraChapaDetalhada } from '../anatomia/BarraChapaDetalhada'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { useDeslocamentoRigido } from './contextoDeformacao'

const LADOS = [
  { sinal: 1, sufixo: 'Esquerda' },
  { sinal: -1, sufixo: 'Direita' },
] as const

/** Painel estrutural: chapa, não barra. */
function Painel({
  id,
  posicao,
  args,
  afastamento = [0, 0, 0],
}: {
  id: string
  posicao: [number, number, number]
  args: [number, number, number]
  afastamento?: [number, number, number]
}) {
  // painéis não dobram: acompanham o campo como peça rígida
  const [dx, dy, dz] = useDeslocamentoRigido(posicao)
  return (
    <Peca id={id} deslocamentoAfastado={afastamento}>
      <mesh position={[posicao[0] + dx, posicao[1] + dy, posicao[2] + dz]}>
        <boxGeometry args={args} />
        <MaterialPeca id={id} />
      </mesh>
    </Peca>
  )
}

/**
 * Membros do monobloco que faltavam à gaiola.
 *
 * A gaiola original tinha o habitáculo — colunas, soleiras, travessa e teto —,
 * mas nada do que fica à frente do corta-fogo, abaixo do assoalho ou atrás do
 * banco traseiro. Para o salvamento é justamente essa parte que decide muita
 * coisa: é onde estão a torre do amortecedor, a longarina que conduz a carga do
 * impacto e o túnel por onde passam escapamento e linhas.
 *
 * A nomenclatura segue a usada em identificação veicular no Brasil — longarina,
 * prolongador, avental, caixa de roda, corta-fogo, soleira, assoalho — para que
 * o aluno encontre na maquete o mesmo nome que ouve na aula.
 */
export function EstruturaMonobloco({ perfil, detalhesAnatomia = false }: { perfil: PerfilVeiculo; detalhesAnatomia?: boolean }) {
  const BarraEstrutural = detalhesAnatomia ? BarraChapaDetalhada : Barra
  const yLongarina = yDaLongarina(perfil)
  const tunel = tunelCentral(perfil)
  const raioCacamba = raioArcoRoda(perfil)
  const zTraseiraUtil = perfil.zonaAbsorcaoTraseira.zDianteiro

  return (
    <group>
      {/* ---------- dianteira ---------- */}

      {/* Painel corta-fogo: a divisa entre cofre do motor e habitáculo, com perfil conformado */}
      <Peca id="paraFogo" deslocamentoAfastado={[0, 0, 0.4]}>
        <mesh position={[0, (perfil.pisoY + perfil.capoY) / 2 + 0.02, zParaFogo(perfil)]}>
          <boxGeometry args={[perfil.largura - 0.16, perfil.capoY - perfil.pisoY + 0.10, 0.04]} />
          <MaterialPeca id="paraFogo" />
        </mesh>
        {/* Saia inferior inclinada da pedaleira / rampa do corta-fogo */}
        <mesh
          position={[0, perfil.pisoY + 0.08, zParaFogo(perfil) - 0.07]}
          rotation={[-0.45, 0, 0]}
        >
          <boxGeometry args={[perfil.largura - 0.18, 0.035, 0.22]} />
          <MaterialPeca id="paraFogo" />
        </mesh>
        {detalhesAnatomia && <group position={[0, (perfil.pisoY + perfil.capoY) / 2 + 0.02, zParaFogo(perfil)]}>
          <PainelEstampadoDetalhado id="paraFogo" largura={perfil.largura - 0.16} altura={perfil.capoY - perfil.pisoY + 0.10} />
        </group>}
      </Peca>

      {/* Caixa de ar: a calha entre a quina do capô e o pé do para-brisa. */}
      <Painel
        id="caixaDeAr"
        posicao={[0, perfil.capoY - 0.04, zCaixaDeAr(perfil)]}
        args={[perfil.largura - 0.24, 0.12, 0.16]}
        afastamento={[0, 0.4, 0.2]}
      />

      {/* Alma / reforço metálico do para-choque dianteiro (crash beam) */}
      <Peca id="almaParaChoqueDianteiro" deslocamentoAfastado={[0, 0, 0.45]}>
        <BarraEstrutural
          id="almaParaChoqueDianteiro"
          de={vetor(xDaLongarina(perfil, 1) + 0.22, yLongarina + 0.14, perfil.frenteZ - 0.12)}
          para={vetor(xDaLongarina(perfil, -1) - 0.22, yLongarina + 0.14, perfil.frenteZ - 0.12)}
          largura={0.12}
          espessura={0.14}
        />
        {/* Curvas nas pontas da alma metálica acompanhando o para-choque */}
        {LADOS.map(({ sinal, sufixo }) => (
          <BarraEstrutural
            key={sufixo}
            id="almaParaChoqueDianteiro"
            de={vetor(xDaLongarina(perfil, sinal) + sinal * 0.22, yLongarina + 0.14, perfil.frenteZ - 0.12)}
            para={vetor(xDaEstrutura(perfil, sinal) - sinal * 0.03, yLongarina + 0.13, perfil.frenteZ - 0.22)}
            largura={0.10}
            espessura={0.13}
          />
        ))}
      </Peca>

      {LADOS.map(({ sinal, sufixo }) => {
        const torre = torreSuspensao(perfil, sinal)
        const xLong = xDaLongarina(perfil, sinal)

        return (
          <group key={`dianteira:${sufixo}`}>
            {/* Torre do amortecedor: base estrutural e cúpula onde a suspensão entrega carga. */}
            <Peca id={`torreSuspensao${sufixo}`} deslocamentoAfastado={[sinal * 0.5, 0.1, 0]}>
              <BarraEstrutural
                id={`torreSuspensao${sufixo}`}
                de={vetor(torre.x, yLongarina, torre.z)}
                para={vetor(torre.x, torre.y - 0.04, torre.z)}
                largura={0.18}
                espessura={0.18}
                cilindrica
              />
              {/* Cúpula estampada superior da torre */}
              <mesh position={[torre.x, torre.y - 0.02, torre.z]}>
                <cylinderGeometry args={[0.11, 0.15, 0.06, 16]} />
                <MaterialPeca id={`torreSuspensao${sufixo}`} metalico={0.5} rugosidade={0.5} />
              </mesh>
              {detalhesAnatomia && <group position={[torre.x, torre.y + 0.02, torre.z]}>
                <FixacoesTorreDetalhadas id={`torreSuspensao${sufixo}`} />
              </group>}
            </Peca>

            {/* Longarina dianteira: conduz a carga do impacto até o corta-fogo. */}
            <Peca id={`longarinaDianteira${sufixo}`} deslocamentoAfastado={[0, -0.3, 0.3]}>
              <BarraEstrutural
                id={`longarinaDianteira${sufixo}`}
                de={vetor(xLong, yLongarina, zParaFogo(perfil))}
                para={vetor(xLong, yLongarina + 0.12, perfil.zonaAbsorcaoDianteira.zTraseiro)}
                largura={0.12}
                espessura={0.14}
              />
            </Peca>

            {/* Prolongador / crash box: o trecho sacrificial que conecta à alma do para-choque. */}
            <Peca id={`prolongadorLongarina${sufixo}`} deslocamentoAfastado={[0, 0, 0.55]}>
              <BarraEstrutural
                id={`prolongadorLongarina${sufixo}`}
                de={vetor(xLong, yLongarina + 0.12, perfil.zonaAbsorcaoDianteira.zTraseiro)}
                para={vetor(xLong, yLongarina + 0.14, perfil.frenteZ - 0.12)}
                largura={0.11}
                espessura={0.13}
              />
            </Peca>

            {/* Avental: liga a torre à extremidade dianteira por cima da roda. */}
            <Peca id={`avental${sufixo}`} deslocamentoAfastado={[sinal * 0.45, 0.2, 0.2]}>
              <BarraEstrutural
                id={`avental${sufixo}`}
                de={vetor(torre.x, torre.y - 0.04, zParaFogo(perfil))}
                para={vetor(torre.x, torre.y - 0.12, perfil.zonaAbsorcaoDianteira.zTraseiro)}
                largura={0.09}
                espessura={0.16}
              />
            </Peca>

            <CaixaDeRoda3D
              id={`caixaRodaDianteira${sufixo}`}
              x={xDaEstrutura(perfil, sinal) - sinal * 0.06}
              y={perfil.raioRoda}
              z={perfil.eixoDianteiroZ}
              raio={raioCacamba}
              afastamento={[sinal * 0.45, 0, 0]}
            />

            {/* ---------- traseira ---------- */}

            <Peca id={`longarinaTraseira${sufixo}`} deslocamentoAfastado={[0, -0.3, -0.3]}>
              <BarraEstrutural
                id={`longarinaTraseira${sufixo}`}
                de={vetor(xLong, yLongarina, zTraseiraUtil)}
                para={vetor(xLong, yLongarina + 0.16, perfil.traseiraZ + 0.14)}
                largura={0.11}
                espessura={0.13}
              />
            </Peca>

            <CaixaDeRoda3D
              id={`caixaRodaTraseira${sufixo}`}
              x={xDaEstrutura(perfil, sinal) - sinal * 0.06}
              y={perfil.raioRoda}
              z={perfil.eixoTraseiroZ}
              raio={raioCacamba}
              afastamento={[sinal * 0.45, 0, 0]}
            />
          </group>
        )
      })}

      {/* Suporte traseiro ligado ao piso, aos braços e aos cubos das duas rodas. */}
      <Peca id="suporteSuspensaoTraseira" deslocamentoAfastado={[0, -0.16, 0]}>
        <group name="suporte-suspensao-traseira">
          <BarraEstrutural id="suporteSuspensaoTraseira"
            de={vetor(xDaLongarina(perfil, -1), perfil.pisoY - 0.04, perfil.eixoTraseiroZ + 0.14)}
            para={vetor(xDaLongarina(perfil, 1), perfil.pisoY - 0.04, perfil.eixoTraseiroZ + 0.14)}
            largura={0.075} espessura={0.08} />
          {LADOS.map(({ sinal, sufixo }) => {
            const xCubo = sinal * (perfil.largura / 2 - perfil.larguraRoda / 2 + 0.02)
            return <group key={sufixo} name={`braco-traseiro-${sufixo}`}>
              <BarraEstrutural id="suporteSuspensaoTraseira"
                de={vetor(xDaLongarina(perfil, sinal), perfil.pisoY - 0.04, perfil.eixoTraseiroZ + 0.14)}
                para={vetor(xCubo, perfil.raioRoda, perfil.eixoTraseiroZ)}
                largura={0.055} espessura={0.07} />
              <BarraEstrutural id="suporteSuspensaoTraseira"
                de={vetor(xDaLongarina(perfil, sinal), perfil.pisoY + 0.03, perfil.eixoTraseiroZ + 0.5)}
                para={vetor(xCubo, perfil.raioRoda, perfil.eixoTraseiroZ)}
                largura={0.06} espessura={0.065} />
              <mesh position={[xDaLongarina(perfil, sinal), perfil.pisoY + 0.03, perfil.eixoTraseiroZ + 0.5]} rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[0.045, 0.045, 0.11, 12]} />
                <MaterialPeca id="suporteSuspensaoTraseira" corBase="#303945" metalico={0.4} />
              </mesh>
            </group>
          })}
        </group>
      </Peca>

      {/* Quadro suporte do motor: berço aparafusado sob o cofre. */}
      <Peca id="subquadroDianteiro" deslocamentoAfastado={[0, -0.55, 0]}>
        {LADOS.map(({ sinal, sufixo }) => (
          <BarraEstrutural
            key={sufixo}
            id="subquadroDianteiro"
            de={vetor(xDaLongarina(perfil, sinal), yLongarina - 0.14, zParaFogo(perfil) - 0.04)}
            para={vetor(xDaLongarina(perfil, sinal), yLongarina - 0.14, perfil.eixoDianteiroZ + 0.24)}
            largura={0.09}
            espessura={0.1}
          />
        ))}
        {[zParaFogo(perfil) - 0.04, perfil.eixoDianteiroZ + 0.24].map((z) => (
          <BarraEstrutural
            key={z}
            id="subquadroDianteiro"
            de={vetor(xDaLongarina(perfil, -1), yLongarina - 0.14, z)}
            para={vetor(xDaLongarina(perfil, 1), yLongarina - 0.14, z)}
            largura={0.08}
            espessura={0.09}
          />
        ))}
      </Peca>

      {/* ---------- assoalho ---------- */}

      {/* Túnel central: corre sobre escapamento e linhas, do corta-fogo ao banco traseiro. */}
      <Painel
        id="tunelCentral"
        posicao={[
          0,
          perfil.pisoY + tunel.altura / 2,
          (zParaFogo(perfil) + perfil.colunaC.zBase) / 2,
        ]}
        args={[tunel.largura, tunel.altura, zParaFogo(perfil) - perfil.colunaC.zBase]}
        afastamento={[0, -0.45, 0]}
      />

      {/* Travessas do assoalho: é sobre elas que os trilhos dos bancos se fixam. */}
      {(
        [
          ['travessaAssoalhoDianteira', perfil.portaDianteira.zDianteiro - 0.12],
          ['travessaAssoalhoTraseira', perfil.colunaB.z - 0.06],
        ] as const
      ).map(([id, z]) => (
        <Peca key={id} id={id} deslocamentoAfastado={[0, -0.4, 0]}>
          <BarraEstrutural
            id={id}
            de={vetor(xDaEstrutura(perfil, -1), perfil.pisoY - 0.03, z)}
            para={vetor(xDaEstrutura(perfil, 1), perfil.pisoY - 0.03, z)}
            largura={0.09}
            espessura={0.1}
          />
        </Peca>
      ))}

      {/* ---------- traseira ---------- */}

      <Painel
        id="assoalhoPortaMalas"
        posicao={[0, perfil.pisoY - 0.04, (perfil.colunaC.zBase + zTraseiraUtil) / 2]}
        args={[perfil.largura - 0.24, 0.05, perfil.colunaC.zBase - zTraseiraUtil]}
        afastamento={[0, -0.4, -0.2]}
      />

      <Painel
        id="painelTraseiro"
        posicao={[0, perfil.pisoY + 0.16, perfil.traseiraZ + 0.12]}
        args={[perfil.largura - 0.18, 0.4, 0.05]}
        afastamento={[0, 0, -0.5]}
      />

      {/* Alma / reforço metálico do para-choque traseiro (crash beam) */}
      <Peca id="almaParaChoqueTraseiro" deslocamentoAfastado={[0, 0, -0.45]}>
        <BarraEstrutural
          id="almaParaChoqueTraseiro"
          de={vetor(xDaLongarina(perfil, 1) + 0.16, yLongarina + 0.16, perfil.traseiraZ + 0.12)}
          para={vetor(xDaLongarina(perfil, -1) - 0.16, yLongarina + 0.16, perfil.traseiraZ + 0.12)}
          largura={0.11}
          espessura={0.13}
        />
      </Peca>
    </group>
  )
}
